import {
  type DemoSessionActivity,
  type GetDemoSessionBreakdownArgs,
  useInfiniteScrollFromCache,
  useLazyGetDemoSessionsBreakdownQuery,
} from '@carboncopyco/data';
import { TextBase, TextCaption, TooltipBubble } from '@carboncopyco/ui';
import {
  formatPlural,
  getTimezoneName,
  secondsToHHmmss,
} from '@carboncopyco/utils';
import { useEffect, useMemo, useState } from 'react';
import { TimestampCol } from '../../../../Dashboard/SessionBreakdownComponents';
import type { CsvParams } from '../../../../Dashboard/SessionBreakdownComponents/types';
import type { Col } from '../../../../FixedTable';
import { useUrlState } from '../../../../utils';
import { DemoLinkFilter } from '../../../Controls/DemoLinkFilter';
import { OpportunitiesFilter } from '../../../Controls/OpportunitiesFilter';
import { SessionsBreakdownTable } from '../../../SessionsBreakdownTable';
import type { UrlState } from '../../../types';
import { useDemoPageProps } from '../../DemoPageContext';
import { ViewedScreens } from '../ViewedScreens';

const cols: Col<DemoSessionActivity>[] = [
  {
    alignment: 'left',
    title: 'Date & time',
    width: 150,
    maxContentWidth: 130,
    render: ({ timestamp }) => <TimestampCol timestamp={timestamp} />,
  },
  {
    alignment: 'left',
    title: 'Visitor',
    width: 160,
    maxContentWidth: 130,
    render: ({ user }) => (
      <div className="flex flex-col h-10 justify-center">
        <TooltipBubble
          title={user?.name}
          placement="top-start"
          disabled={!user?.name}
        >
          <div className="truncate">
            <TextBase
              label={user?.name || 'Anonymous'}
              color="text-ds-natural-600"
              ellipsis
            />
          </div>
        </TooltipBubble>
        <TooltipBubble
          title={user?.email}
          placement="top-start"
        >
          <div className="truncate text-ds-natural">
            <TextCaption
              label={user?.email}
              ellipsis
            />
          </div>
        </TooltipBubble>
      </div>
    ),
  },
  {
    alignment: 'left',
    title: 'Opportunity',
    width: 210,
    maxContentWidth: 190,
    render: ({ forClient }) => {
      const internalClientNames = ['internal', 'anyone'];
      const opportunity = internalClientNames.includes(forClient)
        ? ''
        : forClient;
      return (
        <TooltipBubble
          title={opportunity}
          placement="top-start"
          disabled={!opportunity}
        >
          <div className="truncate">
            <TextBase
              label={opportunity}
              color="text-ds-natural-600"
              ellipsis
            />
          </div>
        </TooltipBubble>
      );
    },
  },
  {
    alignment: 'left',
    title: 'Link name',
    width: 200,
    maxContentWidth: 170,
    render: ({ share }) => (
      <TooltipBubble
        title={share.shareName}
        placement="top-start"
      >
        <div className="truncate">
          <TextBase
            label={share.shareName}
            color="text-ds-natural-600"
            ellipsis
          />
        </div>
      </TooltipBubble>
    ),
  },
  {
    alignment: 'right',
    title: 'Duration',
    width: 80,
    render: (presentation) => (
      <TextBase
        label={secondsToHHmmss(presentation.durationInSeconds)}
        color="text-ds-natural-600"
      />
    ),
  },
  {
    alignment: 'right',
    title: 'Visited screens',
    width: 130,
    render: ({ sessionId, organizationId, workspaceId, screensVisited }) => (
      <ViewedScreens
        sessionId={sessionId}
        organizationId={organizationId}
        workspaceId={workspaceId}
        screensVisited={screensVisited}
      />
    ),
  },
];

export type VisitsSessionsBreakdownProps = {
  setCsvParams: (options: CsvParams) => void;
};

export const VisitsSessionsBreakdown = ({
  setCsvParams,
}: VisitsSessionsBreakdownProps) => {
  const { organizationId, workspaceId, demoId } = useDemoPageProps();
  const [showAll, setShowAll] = useState(false);
  const [urlState, setUrlState] = useUrlState<UrlState>({});
  const { opportunities, links } = urlState;

  const fetchArgs = useMemo<GetDemoSessionBreakdownArgs>(
    () => ({
      organizationId,
      shortSessions: true,
      internalSessions: true,
      workspaceIds: [workspaceId],
      timezone: getTimezoneName(),
      opportunities: opportunities ? opportunities?.split(',') : undefined,
      shareIds: links ? links?.split(',') : undefined,
      demoId,
      withScreenshots: false,
      resourceTypes: ['shared_presentation'],
    }),
    [organizationId, workspaceId, opportunities, links, demoId]
  );

  const {
    items,
    isLoading,
    isLoadingBatch,
    error,
    fetchFirstBatch,
    fetchNextBatch,
    totalSize,
  } = useInfiniteScrollFromCache<
    DemoSessionActivity,
    GetDemoSessionBreakdownArgs
  >(useLazyGetDemoSessionsBreakdownQuery, fetchArgs);

  useEffect(() => {
    fetchFirstBatch();
  }, [fetchArgs, fetchFirstBatch]);

  const clearFilters = () => {
    setUrlState({
      ...urlState,
      opportunities: undefined,
      links: undefined,
    });
  };

  useEffect(() => {
    setCsvParams({
      params: {
        ...fetchArgs,
        resourceTypes: ['shared_presentation'],
      },
      isDisabled: (items && !items.length) || error,
    });
  }, [fetchArgs, items, error, setCsvParams]);

  return (
    <>
      <div className="w-full flex items-center justify-between px-6 py-5">
        <div className="flex gap-3">
          <OpportunitiesFilter
            organizationId={organizationId}
            workspaceId={workspaceId}
            demoId={demoId}
          />
          <DemoLinkFilter
            organizationId={organizationId}
            demoId={demoId}
            workspaceId={workspaceId}
          />
        </div>
        {!!items?.length && (
          <TextCaption
            color="text-ds-natural-500"
            label={`${
              totalSize > 7 && !showAll ? 7 : items.length
            } ${formatPlural('session', items.length)} (out of ${totalSize})`}
          />
        )}
      </div>
      <SessionsBreakdownTable
        showAll={showAll}
        setShowAll={setShowAll}
        totalSize={totalSize}
        onClear={clearFilters}
        error={error}
        handleFetchNextBatch={fetchNextBatch}
        cols={cols}
        data={items}
        isLoading={isLoading}
        isLoadingBatch={isLoadingBatch}
        noResults={(links || opportunities) && !items?.length}
      />
    </>
  );
};
